/**
 * The Help Desk + Tasks spotlight (#1838, slice 3) — stronger than a dot.
 *
 * The screen dims, the button lights up with a gold ring, and one card says
 * what it's for. Office: Help Desk, then "Next: Tasks →". Techs: Help Desk
 * only (their Help Desk is Flag a problem), on the iPad home screen, never
 * mid-visit.
 *
 * When (rules.spotlightDue, decisions 10/9): everyone on first login after
 * the switch is on; again weekly, up to 4 more times, for anyone with no use
 * in the last 30 days; one real use after it first showed stops it. Each
 * showing is saved, so the count follows the person.
 *
 * It points at the real button (`data-teach="helpdesk"` / `"tasks"`); if the
 * button isn't on screen, it waits.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import type { TeachFeature } from "./features";
import { TeachPicture } from "./pictures";
import { spotlightsDue } from "./rules";
import { findTeachTargets } from "./teach-dot";
import { useTeach } from "./teach-state";

const SLOT = "spotlight";

/** A step whose button never shows up is skipped after this long. */
const SKIP_MISSING_MS = 3000;

export function TeachSpotlight() {
    const t = useTeach();
    // Decide once per page load which spotlights are due, so recording a
    // showing doesn't make the next step vanish mid-sequence.
    const [steps, setSteps] = useState<TeachFeature[] | null>(null);
    const [idx, setIdx] = useState(0);
    const [rect, setRect] = useState<DOMRect | null>(null);
    const shown = useRef(new Set<string>());
    const { claim, release, active, spotlightShown, setSpotSettled } = t;

    useEffect(() => {
        if (steps !== null || !t.ready) return;
        if (!t.state.on) {
            setSpotSettled(true);
            return;
        }
        setSteps(spotlightsDue(t.state, t.role, new Date()));
    }, [steps, t.ready, t.state, t.role, setSpotSettled]);

    const allowedHere = t.role === "office" || t.techSpotOk;
    const step = steps ? steps[idx] : undefined;
    // Nothing (left) to show → intro cards may go.
    useEffect(() => {
        if (steps !== null && !step) setSpotSettled(true);
    }, [steps, step, setSpotSettled]);

    // Find the button; wait for it to be on screen.
    const measure = useCallback(() => {
        if (!step || !allowedHere) return setRect(null);
        const el = findTeachTargets().get(step.key);
        setRect(el ? el.getBoundingClientRect() : null);
    }, [step, allowedHere]);
    useEffect(() => {
        measure();
        const id = window.setInterval(measure, 500);
        window.addEventListener("resize", measure);
        return () => {
            window.clearInterval(id);
            window.removeEventListener("resize", measure);
        };
    }, [measure]);

    // On a screen that may carry it but the button isn't there (no Tasks
    // permission, a narrow layout): skip that step rather than wait forever.
    useEffect(() => {
        if (!step || !allowedHere || rect) return;
        const id = window.setTimeout(() => setIdx((i) => i + 1), SKIP_MISSING_MS);
        return () => window.clearTimeout(id);
    }, [step, allowedHere, rect]);

    const ready = !!step && allowedHere && !!rect;
    useEffect(() => {
        if (ready) claim(SLOT);
        else release(SLOT);
    }, [ready, claim, release, active]);
    useEffect(() => () => release(SLOT), [release]);

    const mine = ready && active === SLOT;
    useEffect(() => {
        if (mine && step && !shown.current.has(step.key)) {
            shown.current.add(step.key);
            spotlightShown(step.key);
        }
    }, [mine, step, spotlightShown]);

    const finish = useCallback(() => setSteps([]), []);
    // Later puts off the whole set: the steps not reached yet count as shown
    // too, so they come back together next week instead of on the next page.
    const later = useCallback(() => {
        for (const st of (steps ?? []).slice(idx + 1)) {
            if (!shown.current.has(st.key)) {
                shown.current.add(st.key);
                spotlightShown(st.key);
            }
        }
        setSteps([]);
    }, [steps, idx, spotlightShown]);

    if (!mine || !step || !rect || !step.spotlight) return null;
    const copy = (t.role === "tech" && step.spotlight.tech) || step.spotlight.office;
    const next = steps && idx < steps.length - 1 ? steps[idx + 1] : null;
    return (
        <SpotlightView
            stepKey={step.key}
            eyebrow={step.name}
            headline={copy.headline}
            line={copy.line}
            pic={copy.pic}
            rect={rect}
            nextLabel={next ? `Next: ${next.name} →` : "Got it"}
            onNext={() => (next ? setIdx(idx + 1) : finish())}
            onLater={later}
        />
    );
}

function SpotlightView({
    stepKey,
    eyebrow,
    headline,
    line,
    pic,
    rect,
    nextLabel,
    onNext,
    onLater,
}: {
    stepKey: string;
    eyebrow: string;
    headline: string;
    line: string;
    pic: Parameters<typeof TeachPicture>[0]["pic"];
    rect: DOMRect;
    nextLabel: string;
    onNext: () => void;
    onLater: () => void;
}) {
    const nextRef = useRef<HTMLButtonElement>(null);
    useEffect(() => {
        nextRef.current?.focus();
        const onKey = (e: KeyboardEvent) => e.key === "Escape" && onLater();
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [stepKey, onLater]);

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const pad = 5;
    const width = Math.min(320, vw - 24);
    const centerX = rect.left + rect.width / 2;
    const left = Math.max(12, Math.min(centerX - width + 40, vw - width - 12));
    const roomBelow = vh - rect.bottom;
    const below = roomBelow > 300;
    const top = below ? rect.bottom + 16 : Math.max(12, rect.top - 16 - 300);
    const arrowLeft = Math.max(16, Math.min(centerX - left - 8, width - 32));
    const titleId = `teach-spot-${stepKey}`;

    return createPortal(
        <div className="fixed inset-0 z-[95]" data-testid={`teach-spotlight-${stepKey}`}>
            {/* The ring's huge shadow is the dim layer, so the button shows through. */}
            <div
                aria-hidden
                className="pointer-events-none fixed rounded-xl"
                style={{
                    top: rect.top - pad,
                    left: rect.left - pad,
                    width: rect.width + pad * 2,
                    height: rect.height + pad * 2,
                    // Gold ring, then the dim layer.
                    boxShadow: "0 0 0 3px var(--color-primary), 0 0 0 9999px rgba(12,10,9,0.58)",
                }}
            />
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className="fixed grid gap-2 rounded-xl bg-card p-3.5 text-card-foreground shadow-2xl"
                style={{ top, left, width }}
            >
                <span
                    aria-hidden
                    className={cn("absolute h-4 w-4 rotate-45 bg-card", below ? "-top-2" : "-bottom-2")}
                    style={{ left: arrowLeft }}
                />
                <span className="font-mono text-[11px] font-semibold uppercase tracking-wider text-primary">
                    {eyebrow}
                </span>
                <h4 id={titleId} className="m-0 text-2xl font-extrabold leading-tight tracking-tight">
                    {headline}
                </h4>
                <TeachPicture pic={pic} className="aspect-[16/7]" />
                <p className="m-0 text-[15px]">{line}</p>
                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onLater}
                        className="rounded-md px-3 py-2 text-sm font-semibold text-muted-foreground hover:bg-muted"
                    >
                        Later
                    </button>
                    <button
                        ref={nextRef}
                        type="button"
                        onClick={onNext}
                        className="rounded-md bg-primary px-4 py-2 text-sm font-bold text-primary-foreground hover:bg-primary/90"
                        data-testid="teach-spot-next"
                    >
                        {nextLabel}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
}
